import {
  createContext, useCallback, useContext, useEffect, useId, useMemo, useState,
  type ReactNode,
} from 'react';

/**
 * SP-8a — Filter sidebar context.
 *
 * Permette a qualunque pagina (o sotto-componente) di registrare il proprio
 * pannello di filtri in una sidebar globale tramite un hook `useFilterSidebar`.
 * La sidebar è renderizzata da `<FilterSidebarOutlet />` in `DashboardLayout`.
 *
 * Architettura — stack di slot (non singolo):
 *   - Ogni `useFilterSidebar(node)` pusha uno slot allo stack e lo rimuove
 *     al cleanup (unmount o cambio `node`).
 *   - L'outlet renderizza gli slot in ordine (ultimo pushato in fondo).
 *   - Caso SP-8b: la pagina Pick-list pusha i "filtri testa" allo stack;
 *     quando una pick-list viene espansa, `PickListDetailPanel` pusha
 *     un secondo slot "Filtri articolo" sotto. Smontando l'accordion lo
 *     secondo slot scompare e resta solo quello dei filtri testa.
 *
 * Note di design:
 *   - Niente Outlet di react-router: il context vive in
 *     `<FilterSidebarProvider>` montato in `DashboardLayout`, indipendente
 *     dal routing. Ogni pagina sotto al layout vede lo stesso provider.
 *   - L'`id` slot è auto-generato via `useId` (stable per istanza componente).
 *     Il consumer NON deve gestire la chiave a mano.
 *   - Quando lo stack è vuoto, la sidebar si nasconde (la grid collassa
 *     da 2 colonne a 1 — vedi `DashboardLayout`).
 */

interface SidebarSlot {
  id: string;
  node: ReactNode;
}

interface SidebarCtx {
  slots: SidebarSlot[];
  push: (id: string, node: ReactNode) => void;
  pop: (id: string) => void;
  // SP-8g §7 — collasso manuale della sidebar filtri (desktop). Persistito
  // per sessione in sessionStorage.
  collapsed: boolean;
  toggleCollapsed: () => void;
}

const Ctx = createContext<SidebarCtx>({
  slots: [],
  push: () => {},
  pop: () => {},
  collapsed: false,
  toggleCollapsed: () => {},
});

const COLLAPSE_KEY = 'vp-filters-collapsed';

export function FilterSidebarProvider({ children }: { children: ReactNode }) {
  const [slots, setSlots] = useState<SidebarSlot[]>([]);
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try { return sessionStorage.getItem(COLLAPSE_KEY) === '1'; } catch { return false; }
  });

  const toggleCollapsed = useCallback(() => {
    setCollapsed((c) => {
      const next = !c;
      try { sessionStorage.setItem(COLLAPSE_KEY, next ? '1' : '0'); } catch { /* no-op */ }
      return next;
    });
  }, []);

  const push = useCallback((id: string, node: ReactNode) => {
    setSlots((cur) => {
      const existing = cur.find((s) => s.id === id);
      // Difesa contro infinite loop quando il consumer NON memoizza il `node`:
      // se l'id è già registrato con la stessa reference di node, niente
      // setState (ritorniamo `cur` invariato → React skip-pa il re-render).
      if (existing && existing.node === node) return cur;
      const without = cur.filter((s) => s.id !== id);
      return [...without, { id, node }];
    });
  }, []);

  const pop = useCallback((id: string) => {
    setSlots((cur) => cur.filter((s) => s.id !== id));
  }, []);

  const value = useMemo<SidebarCtx>(
    () => ({ slots, push, pop, collapsed, toggleCollapsed }),
    [slots, push, pop, collapsed, toggleCollapsed],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/**
 * Registra `node` come slot della filter sidebar per la vita del componente
 * chiamante. Quando il componente unmount-a, lo slot viene rimosso.
 *
 * Quando `node` cambia (es. nuovi value/onChange), il render successivo
 * sovrascrive lo slot esistente con lo stesso `id`. Usa `useMemo` o stable
 * refs per evitare re-render eccessivi se passi inline JSX.
 *
 * Pattern d'uso:
 * ```tsx
 * function MyPage() {
 *   const [search, setSearch] = useState('');
 *   const filters = useMemo(
 *     () => <PageFilters>...</PageFilters>,
 *     [search],
 *   );
 *   useFilterSidebar(filters);
 *   // ...
 * }
 * ```
 *
 * Passare `null` come `node` rimuove esplicitamente lo slot (utile per
 * disattivare la sidebar in condizioni runtime).
 */
export function useFilterSidebar(node: ReactNode | null): void {
  const id = useId();
  const { push, pop } = useContext(Ctx);

  useEffect(() => {
    if (node === null || node === undefined) {
      pop(id);
      return;
    }
    push(id, node);
    return () => pop(id);
  }, [id, node, push, pop]);
}

/**
 * Renderizza tutti gli slot registrati. Da usare in `DashboardLayout`
 * dentro il container `<aside>` della sidebar.
 *
 * Se nessuno slot è registrato ritorna `null` (il layout collassa la
 * colonna della sidebar a 0px tramite CSS `--filter-w`).
 */
export function FilterSidebarOutlet(): ReactNode {
  const { slots } = useContext(Ctx);
  if (slots.length === 0) return null;
  return (
    <>
      {slots.map((s, i) => (
        <div key={s.id} className="vp-filter-sidebar__slot" data-slot-index={i}>
          {s.node}
        </div>
      ))}
    </>
  );
}

/**
 * Hook utility per sapere se la sidebar è attualmente popolata.
 * Usato da `DashboardLayout` per decidere se renderizzare la colonna
 * della sidebar o lasciare il contenuto a tutta larghezza.
 */
export function useHasFilterSidebar(): boolean {
  const { slots } = useContext(Ctx);
  return slots.length > 0;
}

/**
 * SP-8g §7 — stato di collasso della sidebar filtri + toggle. Usato dal
 * pulsante mostra/nascondi nell'area contenuto e da `FilterSidebarPanel`.
 */
export function useFilterSidebarCollapsed(): { collapsed: boolean; toggle: () => void } {
  const { collapsed, toggleCollapsed } = useContext(Ctx);
  return { collapsed, toggle: toggleCollapsed };
}
